import React, { useEffect, useRef, useState } from "react";
import {
    Box,
} from "@mui/material";
import { useRemindMeContext } from "../../context/RemindMeContext";
import { useDeviceId } from "../../hooks/useDeviceId";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../context/AlertContext";
import { companyId } from "../../utils/remindMeServices/RemindMeAPIServices";
import { sendEmailImage } from "../../utils/remindMeServices/ImageToMail";
import ImageGridContainer from "../../components/remindMe/ImageGridContainer";
import CameraButtonContainer from "../../components/remindMe/CameraButtonContainer";
import CommonLoader from "../../components/remindMe/CommonLoader";
import BottomSendContainer from "../../components/remindMe/BottomSendContainer";
import NoEmailDialog from "../../components/remindMe/NoEmailDialog";
import InnerTopBar from "../../components/layout/InnerTopBar";

const SendImageScreen: React.FC = () => {
    const { selectedEmail } = useRemindMeContext();
    const [selectedImages, setSelectedImages] = useState<{ uri: string; id: string }[]>([]);
    const imageUris = selectedImages.map(image => image.uri);
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { deviceId } = useDeviceId();
    const navigate = useNavigate();
    const { showAlert } = useAlert();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!selectedEmail) {
            setIsDialogOpen(true);
        }
    }, [selectedEmail]);

    useEffect(() => {
        setIsSendButtonEnabled(selectedImages.length > 0);
    }, [selectedImages]);

    const openCamera = () => {
        console.log("Camera open!");
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const files = Array.from(event.target.files);

            const readFileAsDataURL = (file: File) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });

            Promise.all(files.map((file) => readFileAsDataURL(file)))
                .then((base64Images) => {
                    const newImages = base64Images.map((base64) => ({
                        uri: base64,
                        id: Date.now().toString() + Math.random().toString(), // Unique ID
                    }));
                    setSelectedImages((prevImages) => [...prevImages, ...newImages].slice(0, 6));
                })
                .catch((error) => {
                    console.error("Error reading files:", error);
                });
        }
    };


    const removeImage = (id: string) => {
        setSelectedImages((prevImages) => prevImages.filter((image) => image.id !== id));
    };

    const handleSend = async (selectedEmail: string) => {
        if (selectedImages.length === 0) {
            showAlert('Please select images to send.', 'warning');
            return;
        }
        if (!deviceId) {
            return;
        }
        const companyIdNumber = companyId ? Number(companyId) : NaN;

        try {
            setIsLoading(true);
            await sendEmailImage(
                selectedEmail,
                imageUris,
                'image',
                companyIdNumber,
                deviceId,
            )
            showAlert(`Success! Email sent to ${selectedEmail}`, 'success');

            navigate('/home');
        } catch (error) {
            console.log(error);
            showAlert('Oops! Sending email failed. Please check your data connection.', 'error');
        }
        finally {
            setIsLoading(false)
        }
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        navigate('/settings');
    };

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center", flex: 1,
            width: '100%',
        }}>
            <InnerTopBar title="Send Image" />
            {isLoading ? (
                <CommonLoader isVisible={isLoading} />
            ) : (
                <>
                    {/* Camera Icon */}
                    <CameraButtonContainer onClick={openCamera} />

                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImageUpload}
                    />

                    {/* Image Grid */}
                    <ImageGridContainer selectedImages={selectedImages} handleImageUpload={handleImageUpload} removeImage={removeImage} />

                </>
            )}

            {/* Bottom Section */}
            < BottomSendContainer
                handleSend={handleSend}
                isSendEnabled={isSendButtonEnabled}
            />

            {/* Dialog */}
            <NoEmailDialog isDialogOpen={isDialogOpen} handleCloseDialog={handleCloseDialog} />

        </Box >
    );
};

export default SendImageScreen;
